/**
 * MessageList - Message container (scroll handled by parent)
 */

import React, { useEffect } from 'react';
import { Message } from '../types/chat';
import { MessageBubble } from './MessageBubble';
import { MessageListSkeleton } from './MessageListSkeleton';
import { isFirstInGroup } from '../utils/groupMessages';

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

export const MessageList: React.FC<MessageListProps> = ({ messages, isLoading, scrollContainerRef }) => {
  // Auto-scroll to bottom on new messages or when loading completes
  // Use parent scroll container ref
  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el || isLoading) return;

    requestAnimationFrame(() => {
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    });
  }, [messages.length, isLoading, scrollContainerRef]);

  if (isLoading && messages.length === 0) {
    // Only show skeleton on initial load (no cached data)
    return <MessageListSkeleton />;
  }

  if (messages.length === 0 && !isLoading) {
    // Only show empty state if we successfully fetched and got 0 results
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <p>No messages yet</p>
          <p className="text-sm mt-1">Send a message to start the conversation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-1">
      {messages.map((message, index) => {
        const showAvatar = isFirstInGroup(messages, index);
        const showName = !message.isMine && showAvatar;

        return (
          <MessageBubble
            key={message.id}
            message={message}
            showAvatar={showAvatar}
            showName={showName}
          />
        );
      })}
    </div>
  );
};

